import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useToast } from '../../components/ui/use-toast';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ShieldCheck } from 'lucide-react';

// İlk girişte zorunlu şifre değişimi ekranı (kurumsal mavi).
// Yalnızca user.must_change_password === true iken gösterilir.
const ForcePasswordChangePage = () => {
  const { user, updateUser, logout } = useAuthStore();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({ title: 'Hata', description: 'Tüm alanları doldurun.', variant: 'destructive' });
      return;
    }
    if (newPassword.length < 6) {
      toast({ title: 'Hata', description: 'Yeni şifre en az 6 karakter olmalı.', variant: 'destructive' });
      return;
    }
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      toast({
        title: 'Hata',
        description: 'Şifre en az bir küçük harf, bir büyük harf ve bir rakam içermeli.',
        variant: 'destructive',
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: 'Hata', description: 'Yeni şifreler eşleşmiyor.', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      await api.put('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      // Bayrağı yerelde de temizle ki yönlendirme tamamlansın.
      updateUser({ must_change_password: false });
      toast({ title: 'Başarılı', description: 'Şifreniz güncellendi.', variant: 'success' });
      navigate('/dashboard', { replace: true });
    } catch (error) {
      toast({
        title: 'Hata',
        description: error.response?.data?.error || 'Şifre güncellenemedi.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-700 via-blue-600 to-blue-800 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-blue-600 px-6 py-5 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15">
            <ShieldCheck className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-white text-lg font-bold leading-tight">Şifre Güncelleme</h1>
            <p className="text-blue-100 text-xs">İlk girişte şifrenizi belirlemeniz gerekiyor</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-sm text-slate-500">
            Merhaba <span className="font-medium text-slate-700">
              {user?.first_name} {user?.last_name}
            </span>, hesabınızı güvene almak için lütfen yeni bir şifre belirleyin.
          </p>

          <div className="space-y-1.5">
            <Label htmlFor="current" className="text-sm font-medium text-slate-700">
              Mevcut (geçici) şifre
            </Label>
            <Input
              id="current"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              className="h-10"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new" className="text-sm font-medium text-slate-700">
              Yeni şifre
            </Label>
            <Input
              id="new"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              className="h-10"
            />
            <p className="text-[11px] text-slate-400">
              En az 6 karakter; küçük harf, büyük harf ve rakam içermeli.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirm" className="text-sm font-medium text-slate-700">
              Yeni şifre (tekrar)
            </Label>
            <Input
              id="confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              className="h-10"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-blue-600 hover:bg-blue-700 shadow-sm text-base"
          >
            {loading ? 'Güncelleniyor...' : 'Şifreyi Güncelle ve Devam Et'}
          </Button>

          <button
            type="button"
            onClick={async () => {
              await logout();
              navigate('/login', { replace: true });
            }}
            className="w-full text-center text-xs text-slate-400 hover:text-slate-600"
          >
            Çıkış yap
          </button>
        </form>
      </div>
    </div>
  );
};

export default ForcePasswordChangePage;
